(ns machtfit.startpage.search
  (:require [clojure.string :as str]
            [machtfit.api :as api]
            [machtfit.errors :as errors]
            [machtfit.repository.products :as products]
            [machtfit.startpage.filter :as filter]
            [re-frame.core :as rf]))

(def db-slots
  [:startpage :search])

;; Must match mf.api.search_sort.SORT_MODES.
(def sort-modes
  #{:distance :price-asc :price-desc :recommended :date-published})

(defn server-only?
  "Whether the render path reads server-decided results. The endpoint requires an
  origin, so until one resolves the client stays on its own filter/rank path
  rather than rendering nothing."
  [db]
  (boolean (get-in db [:products-location :coordinates])))

(rf/reg-sub ::server-only?
  :<- [:get [:products-location :coordinates]]
  (fn [origin _]
    (boolean origin)))

(defn slot-for
  "Which stored search a page reads and writes.

  With an origin, every page keeps its own slot. The split dates from the CLJS
  product list, which sent its own request from `/angebote-new/`. Every page now
  sends the same `server-request`, so a shared slot could no longer carry one
  page's results into another page's render, and only the catalogue renders a slot
  at all.

  Without one there is exactly one search -- only `::navigate-to-catalog` posts
  one, and it runs while the page is `:catalog` -- so every page reads the slot
  that can hold it."
  [on-server? page-id]
  (if on-server? (or page-id :catalog) :catalog))

(defn db-path [db]
  (conj db-slots (slot-for (server-only? db) (get-in db [:ui :page :id]))))

(defn server-request
  "The extended-search request for the current filter+sort state, or nil when no
  origin is resolved yet (the endpoint requires one).

  The term comes off the same effective filter as the rest of the request rather
  than from the caller: the endpoint reads `:term` and ignores a `:keyword`
  filter, so a caller passing a term that disagrees with the filter it ships
  alongside turned a keyword search into a whole-catalogue browse. Deriving both
  from one place makes that disagreement impossible instead of relying on every
  call site to pass the right thing."
  [db]
  (let [non-empty (filter/db->non-empty-categories db)
        effective (filter/db->effective-filter db non-empty)
        origin (get-in db [:products-location :coordinates])]
    (when origin
      {:term (or (:keyword effective) "")
       :filters (-> effective (dissoc :sort-by :keyword) (assoc :origin origin))
       :sort (get sort-modes (:sort-by effective) :recommended)})))

(defn result->search-state
  "Stored search state for one response ``result``, keyed by the ``query`` that
  asked for it. ``:rank-by-id`` maps id -> rank, 0 = most relevant."
  [server? query {:keys [relaxed? products engine product-type-counts truncated-at total-count]}]
  (merge {:term (:term query)
          :query query
          :relaxed? relaxed?
          :engine engine
          :truncated-at truncated-at
          :total-count total-count
          :status :done}
         (let [decoded (products/payload->products products)]
           (if server?
             {:server-products (into {} (map (juxt :id identity)) decoded)
              :order (mapv :id decoded)
              :product-type-counts product-type-counts}
             (let [ids (mapv :id decoded)]
               {:ids (set ids)
                :rank-by-id (zipmap ids (range))})))))

(defn current?
  "Whether ``sent`` is still the request ``slot`` is waiting for.

  The slot is the one that issued the request, not the one the page happens to
  show when the response lands: every slot starts its counter at 1, so
  recomputing it here would let one page's response settle into another's."
  [db slot sent]
  (= (:sent (get-in db slot)) sent))

(rf/reg-event-db ::store
  (fn [db [_ server? query slot sent result]]
    (if (current? db slot sent)
      (assoc-in db slot
                (assoc (result->search-state server? query result) :sent sent))
      db)))

(defn fail-fx
  "The fx for a request that did not answer with anything renderable.

  Only the server-only grid renders what the request decided, so only there is a
  failure an app error. Without an origin the search narrows a catalogue the
  client already holds, and `::api-error-received` would replace the whole SPA with the
  error panel for the rest of the session, because nothing clears `:error?`."
  [db server? slot sent reason]
  (let [current (get-in db slot)]
    (if (current? db slot sent)
      {:db (assoc-in db slot (assoc current :status :failed))
       :fx (cond-> [[::errors/capture-with-sentry
                     {:type "SearchRequestFailed"
                      :message (str "search request failed: " (name reason))
                      :extra-data {:query (:query current)}}]]
             server?
             (conj [:dispatch [::api/api-error-received]]))}
      {:db db})))

(rf/reg-event-fx ::fail
  (fn [{:keys [db]} [_ server? slot sent reason]]
    (fail-fx db server? slot sent reason)))

(defn response-event
  "The event a search response settles into. A success without `:products` is a
  failure too: every path renders from the products."
  [server? request slot sent payload]
  (let [result (:result payload)]
    (cond
      (not (:success payload))
      [::fail server? slot sent :rejected]

      (some? (:products result))
      [::store server? request slot sent result]

      :else
      [::fail server? slot sent :unrenderable])))

(defn- invalidated
  "An emptied slot whose counter has moved past anything in flight for it.

  The counter is what tells a stale response from the current one. Replacing the
  slot with nil restarts it at 1, so a response already numbered 1 matches the
  slot the next request opens; advancing it instead retires that response."
  [slot]
  {:sent (inc (or (:sent slot) 0))})

(defn clear-db [db]
  (update-in db (db-path db) invalidated))

(rf/reg-event-db ::clear
  (fn [db _]
    (clear-db db)))

(defn fetch-fx
  [db term request force?]
  (let [server? (server-only? db)
        slot (db-path db)
        current (get-in db slot)]
    (cond
      (and (not server?) (str/blank? term))
      {:dispatch [::clear]}

      (nil? request)
      {}

      ;; With an origin, ``:failed`` is retryable, so the same filter state is
      ;; re-asked for on the next navigation rather than leaving the grid empty
      ;; for the session. Without one the result only narrows a catalogue the
      ;; client already holds, so the pre-cutover skip set stands.
      (and (not force?)
           (= request (:query current))
           ((if server? #{:done :pending} #{:done :pending :failed}) (:status current)))
      {}

      :else
      ;; A monotonic counter, not the request map: a forced refetch deliberately
      ;; re-sends an identical map, so map equality cannot tell a stale response
      ;; from the current one.
      (let [sent (inc (or (:sent current) 0))
            pending {:term (:term request) :query request :status :pending :sent sent}]
        {:db (assoc-in db slot
                       ;; Merged on the server path so the previous results stay
                       ;; on screen while the new request is in flight.
                       (if server? (merge current pending) pending))
         :post (api/bare-request
                db
                {:data `[(:search ~request :search-result)]
                 :on-error #(rf/dispatch [::fail server? slot sent :transport])
                 :on-success
                 (fn [response]
                   (rf/dispatch (response-event server? request slot sent
                                                (-> response :result :search-result))))})}))))

(rf/reg-event-fx ::fetch
  (fn [{:keys [db]} [_ raw-term {:keys [force?]}]]
    (let [term (or raw-term "")]
      (fetch-fx db
                term
                (if (server-only? db) (server-request db) {:term term})
                (boolean force?)))))

(rf/reg-sub ::data
  :<- [:get db-slots]
  :<- [:get [:ui :page :id]]
  :<- [::server-only?]

  (fn [[by-page page-id on-server?] _]
    (get by-page (slot-for on-server? page-id))))
